import * as React from 'react';
import { FC } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { DropdownButton } from 'react-bootstrap';
import type { YoAuthUser, YoProject } from '@yakoffice/yakoffice-firebase';
import styles from './yo-project-menu.module.sass';
import { useLocation, useParams } from 'react-router-dom';
import { getProjectNameAndImage } from './getProjectNameAndImage';
import {
    compareProjectIdWithRouteParamProjectId,
    determineIfPublisherProjectId,
    getProjectUrl
} from '@yakoffice/helpers';
import { mapPublisherProjectIdToProjectIdString } from '../../../../navigation-rail/src/lib/helpers';
import { ProjectLogo } from '../project-logo/project-logo';

export interface YoProjectMenuProps {
    authUser: YoAuthUser;
    yoProjects: YoProject[];
    toolId: string;
    isDevSite: boolean;
    hideText?: boolean;
}

export const YoProjectMenu: FC<YoProjectMenuProps> = (props) => {

    const routeParams = useParams<{ projectId: string }>();
    const location = useLocation();

    const checkIfAuthorisedForToolNavigation = (projectId: string, toolId: string) => {
        return props.authUser.isAuthorisedForTool(mapPublisherProjectIdToProjectIdString(projectId), toolId);
    };

    const pathUpdateOrRedirect = (toolId: string, toolProjectId: string) => {

        if (toolId === 'publisher')
            return window.location.href = `${props.isDevSite ? 'http://localhost:3000' : 'https://publisher.yakoffice.app'}${getProjectUrl(toolId, toolProjectId)}`;

        if (toolId === props.toolId)
            return window.location.href = location.pathname.replace(`/${routeParams.projectId}/`, `/${toolProjectId}/`);
        else
            return window.location.href = (`${props.isDevSite ? `https://${toolId}-yakoffice-dev.web.app` : `https://${toolId}.yakoffice.app`}${getProjectUrl(toolId, toolProjectId)}`);
    };

    const defaultNavigation = (project: YoProject) => {

        const buildsTool = project.tools.find(tool => tool.toolId === 'builds');

        if (buildsTool !== undefined)
            pathUpdateOrRedirect('builds', buildsTool.toolProjectId);
        else
            pathUpdateOrRedirect('portal', project.id);
    };

    function navigateToUpdatedProject(project: YoProject) {

        if (props.toolId == 'portal') {

            if (window.location.pathname === '/users')
                return window.location.href = location.pathname.replace('/users', `/projects/${project.id}/tools`);

            if (window.location.pathname === '/projects')
                return window.location.href = location.pathname.replace('/projects', `/projects/${project.id}/tools`);

            const iamRolesForTargetProject = props.authUser.yoUser?.iamRoles.find(iamRole => iamRole.projectId === project.id);

            if (iamRolesForTargetProject !== undefined && iamRolesForTargetProject.roles.length > 0)
                return window.location.href = location.pathname.replace(`/${routeParams.projectId}/`, `/${project.id}/`);
        }

        let foundTool = project.tools.find(tool => tool.toolId.toLocaleLowerCase() === props.toolId.toLocaleLowerCase());

        if (foundTool !== undefined) {
            if (checkIfAuthorisedForToolNavigation(project.id, foundTool.toolId))
                pathUpdateOrRedirect(foundTool.toolId, foundTool.toolProjectId);
            else
                defaultNavigation(project);
        } else {

            for (let i = 0; i < project.tools.length; i++) {

                if (checkIfAuthorisedForToolNavigation(project.id, project.tools[i].toolId))
                    return pathUpdateOrRedirect(project.tools[i].toolId, project.tools[i].toolProjectId);
            }
            defaultNavigation(project);
        }
    }

    const projectInfo = () => {

        const projectInfo = getProjectNameAndImage(routeParams.projectId, props.yoProjects.find(project => determineIfPublisherProjectId(routeParams.projectId) == project.id));

        return <>{projectInfo.projectImage}{!props.hideText && <span>{projectInfo.projectName}</span>}</>;
    };

    return props.authUser && props.authUser.yoUser && props.authUser.yoUser?.projects.length > 0 && props.yoProjects.length > 0 ?
        <DropdownButton drop="end"
                        variant="link"
                        className={`${styles.projectMenu} projectMenu`}
                        title={projectInfo()}>

            {props.yoProjects.map(project => {
                return (
                    <Dropdown.Item key={project.id} as="button"
                                   className="text-capitalize nav-link navigationRailNavItem"
                                   onClick={() => navigateToUpdatedProject(project)}
                                   active={compareProjectIdWithRouteParamProjectId(project.id, routeParams.projectId)}>
                        <ProjectLogo src={project.imageUrl}
                                     alt={project.name} /> {project.name}
                    </Dropdown.Item>
                );
            })}
        </DropdownButton> : null;
};
